import { useEffect } from "react";
import { Link } from "react-router";

import { ProductAuth, ProductSkillCheck, ProductSkillPanel } from "@/assets/images";
import { AccountMenu } from "@/components/layouts";
import { Card, CardContent } from "@/components/ui/Card";
import { Notice } from "@/components/ui/Notice";
import { CONFIG } from "@/config";
import { routePaths } from "@/config/routes";
import { useAuthContext } from "@/context/auth";
import { useSessionStorage } from "@/hooks";
import { useCanAccessUserManagerApp } from "@/hooks/useCanAccess";
import {
  getBlockedRedirectUrl,
  productLabelFromUrl,
  recordAutoRedirect,
} from "@/lib/redirect-loop";

const ProductCard = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Card
    className="w-64 max-w-full bg-background/95 backdrop-blur-md hover:shadow-elegant transition-shadow duration-300 cursor-pointer h-full"
    asChild
  >
    <Link to={to}>
      <CardContent className="flex flex-col items-center justify-center p-12 space-y-6">
        {children}
      </CardContent>
    </Link>
  </Card>
);

const useRedirectUrl = () => {
  const [redirectUrl] = useSessionStorage<string>("redirectUrl");
  const [redirectUrlTimestamp] = useSessionStorage<number>("redirectUrlTimestamp");
  const { user } = useAuthContext();

  if (typeof redirectUrl !== "string" || !redirectUrl) {
    return null;
  }

  if (!redirectUrlTimestamp || Date.now() - redirectUrlTimestamp > 60_000) {
    return null;
  }

  if (
    (redirectUrl.startsWith(CONFIG.TALENT_BOOST_URL) && user?.products.includes("BOOST")) ||
    (redirectUrl.startsWith(CONFIG.TALENT_SCORE_URL) && user?.products.includes("SCORE"))
  ) {
    return redirectUrl;
  }

  return null;
};

export const MainView = () => {
  const canAccessUserManagerApp = useCanAccessUserManagerApp();
  const { user, isError } = useAuthContext();
  const isLoaded = !!user;
  const redirectUrl = useRedirectUrl();

  useEffect(() => {
    // Remove any redirect_url or error params that propagated here via backend redirects.
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("redirect_url");
    currentUrl.searchParams.delete("error");

    window.history.pushState({}, document.title, currentUrl.toString());
  }, []);

  useEffect(() => {
    // We redirect ONLY if the user is REALLY logged in, to avoid redirect loops.
    // We cannot rely on isLoggedIn from useAuthContext(), because it may happen
    // that id_token is present but the access_token expired (or is invalid).
    if (isLoaded && redirectUrl) {
      // We don't use clear function from useSessionStorage() hook to avoid rerender
      sessionStorage.removeItem("redirectUrl");
      sessionStorage.removeItem("redirectUrlTimestamp");

      console.debug(`[MainView] Redirecting to ${redirectUrl}`);
      recordAutoRedirect(redirectUrl);
      window.location.href = redirectUrl;
    }
  }, [isLoaded, redirectUrl]);

  if (isError) {
    console.debug(
      `[MainView] Failed to fetch user in AuthContext, redirecting to login` +
        (redirectUrl ? ` (${redirectUrl})` : ""),
    );
    window.location.href =
      "/login?error=authContext" +
      (redirectUrl ? `&redirect_url=${encodeURIComponent(redirectUrl)}` : "");
    return null;
  }

  if (redirectUrl) {
    return null;
  }

  if (!isLoaded) {
    return null; // TODO Loader
  }

  const showTalentScore = user.products.includes("SCORE");
  const showTalentBoost = user.products.includes("BOOST");
  const visibleProductsCount = Number(showTalentScore) + Number(showTalentBoost);
  const blockedRedirectUrl = getBlockedRedirectUrl();

  // If user has access to only one product and no access to User Manager,
  // we redirect them directly to that product. Skipped when the product just
  // rejected the user (blocked redirect) - redirecting again would loop.
  if (visibleProductsCount === 1 && !canAccessUserManagerApp && !blockedRedirectUrl) {
    console.debug("[MainView] User has access to only one product, redirecting directly");
    if (showTalentScore) {
      recordAutoRedirect(CONFIG.TALENT_SCORE_URL);
      window.location.href = CONFIG.TALENT_SCORE_URL;
    } else if (showTalentBoost) {
      recordAutoRedirect(CONFIG.TALENT_BOOST_URL);
      window.location.href = CONFIG.TALENT_BOOST_URL;
    } else {
      window.location.href = "https://skillpanel.com"; // Should never happen
    }
    return null;
  }

  return (
    <div className="front-door min-h-screen flex flex-col bg-gradient-panel">
      <header className="flex justify-end px-6 py-4">
        <AccountMenu />
      </header>

      <div className="flex flex-1 flex-col items-center justify-center p-8 w-full max-w-full md:max-w-4xl mx-auto space-y-12 text-center pb-24">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold tracking-tight text-brand">Welcome to SkillPanel</h1>
          <p className="text-2xl text-brand">Choose your product</p>
        </div>

        {blockedRedirectUrl && (
          <Notice tone="error" role="alert" className="mx-auto max-w-xl">
            You don't have access to {productLabelFromUrl(blockedRedirectUrl)}. Please contact your
            administrator.
          </Notice>
        )}

        <div className="flex flex-wrap justify-center gap-8 mx-auto">
          {showTalentScore && (
            <ProductCard to={CONFIG.TALENT_SCORE_URL}>
              <ProductSkillCheck />
            </ProductCard>
          )}

          {showTalentBoost && (
            <ProductCard to={CONFIG.TALENT_BOOST_URL}>
              <ProductSkillPanel />
            </ProductCard>
          )}

          {canAccessUserManagerApp && (
            <ProductCard to={routePaths.USER_MANAGER_APP.USERS_VIEW_ABSOLUTE}>
              <div className="flex flex-col items-center justify-center w-full aspect-square">
                <ProductAuth width={48} height={48} />
                <strong className="block mt-1 font-medium text-2xl text-brand">
                  <span className="font-bold">User</span>Manager
                </strong>
              </div>
            </ProductCard>
          )}
        </div>
      </div>
    </div>
  );
};
